import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { ScanFace, ChevronsRight, Check, Loader2 } from "lucide-react";
import { passkeysSupported, stepUpApprove, passkeyError } from "@/lib/passkeys";

const THRESHOLD = 0.82;

/**
 * Swipe right to confirm, or approve with Face ID. Deliberate by design: a real
 * order should take a gesture, not a stray click.
 */
export const SwipeConfirm = ({ label, disabled, submitting, onConfirm, tone = "buy",
  faceIdPayload }) => {
  const track = useRef(null);
  const [x, setX] = useState(0);
  const [done, setDone] = useState(false);
  const [faceBusy, setFaceBusy] = useState(false);
  const dragging = useRef(false);

  const width = () => (track.current?.offsetWidth || 240) - 52;
  const busy = submitting || faceBusy || disabled;

  const move = (clientX, startX) => {
    const next = Math.max(0, Math.min(width(), clientX - startX));
    setX(next);
  };

  const start = (e) => {
    if (busy || done) return;
    dragging.current = true;
    const startX = (e.touches?.[0]?.clientX ?? e.clientX) - x;
    const onMove = (ev) => {
      if (!dragging.current) return;
      move(ev.touches?.[0]?.clientX ?? ev.clientX, startX);
    };
    const onUp = (ev) => {
      dragging.current = false;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
      const final = Math.max(0, Math.min(width(), (ev.changedTouches?.[0]?.clientX ?? ev.clientX) - startX));
      if (final >= width() * THRESHOLD) {
        setX(width());
        setDone(true);
        onConfirm();
      } else {
        setX(0);
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onUp);
  };

  const faceId = async () => {
    setFaceBusy(true);
    try {
      const token = await stepUpApprove("confirm_order", faceIdPayload);
      setDone(true);
      onConfirm(token);
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.info("No Face ID on this device yet",
          { description: "Add a passkey in Profile → Security, then you can approve with Face ID." });
      } else {
        toast.error("Face ID approval failed", { description: passkeyError(e) });
      }
      setFaceBusy(false);
    }
  };

  const pct = Math.min(1, x / Math.max(1, width()));
  const green = tone === "buy";

  return (
    <div className="space-y-2">
      <div ref={track} onPointerDown={start} onTouchStart={start}
        data-testid="swipe-confirm"
        className={`relative h-12 select-none overflow-hidden rounded-full border touch-none ${
          busy ? "opacity-60" : ""} ${green ? "bg-emerald-600/10 border-emerald-600/30"
                                            : "bg-red-500/10 border-red-500/30"}`}>
        <div className={`absolute inset-y-0 left-0 transition-[width] duration-75 ${
          green ? "bg-emerald-600/20" : "bg-red-500/20"}`} style={{ width: x + 52 }} />
        <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold pointer-events-none"
          style={{ opacity: 1 - pct }}>
          {submitting ? "Placing..." : done ? "Confirmed" : label}
        </span>
        <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold pointer-events-none"
          style={{ opacity: pct }}>
          Release to confirm
        </span>
        <button type="button" disabled={busy} onKeyDown={(e) => {
            if (["Enter", " "].includes(e.key) && !busy) { setDone(true); onConfirm(); }
          }}
          data-testid="swipe-confirm-thumb"
          className={`absolute top-1 bottom-1 left-1 w-12 rounded-full flex items-center justify-center text-white shadow-premium transition-transform ${
            green ? "bg-emerald-600" : "bg-red-500"}`}
          style={{ transform: `translateX(${x}px)` }}>
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" />
            : done ? <Check className="h-4 w-4" /> : <ChevronsRight className="h-4 w-4" />}
        </button>
      </div>

      {passkeysSupported() && (
        <button type="button" onClick={faceId} disabled={busy || done}
          data-testid="face-id-confirm"
          className="w-full h-10 rounded-full border text-sm font-medium inline-flex items-center justify-center gap-2 hover:bg-accent transition-colors disabled:opacity-60">
          {faceBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanFace className="h-4 w-4" />}
          {faceBusy ? "Waiting for Face ID..." : "Or approve with Face ID"}
        </button>
      )}
    </div>
  );
};
